import React from 'react';
import PropTypes from 'prop-types';
import {Helmet} from 'react-helmet-async';
import Loader from '@/components/Loader';
import RemoveLoadingScreen from '@/pages/RemoveLoadingScreen';
import {containerPx} from '@/features/microsite/constants';
import CtaGroup from './CtaGroup';
import MarketingContent from './MarketingContent';
import MarketingFooter from './MarketingFooter';
import MicrositeFaqSection from './MicrositeFaqSection';
import MicrositeHero from './MicrositeHero';
import MicrositeHeroCards from './MicrositeHeroCards';
import TopNavbar from './TopNavbar';

export function scrollToForm() {
  document.getElementById('interest-form')?.scrollIntoView({behavior: 'smooth', block: 'start'});
}

MicrositeShell.propTypes = {
  pageTitle: PropTypes.string,
  primaryHref: PropTypes.string,
  primaryLabel: PropTypes.node,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  notFoundMessage: PropTypes.node,
  hasData: PropTypes.bool,
  hero: PropTypes.shape({
    image: PropTypes.string,
    headingPrefix: PropTypes.node,
    headingHighlight: PropTypes.node,
    subtitlePrefix: PropTypes.node,
    subtitleBold: PropTypes.node,
    pills: PropTypes.node
  }),
  description: PropTypes.node,
  showCtas: PropTypes.bool,
  primaryCta: PropTypes.shape({
    body: PropTypes.node,
    href: PropTypes.string
  }),
  interestForm: PropTypes.shape({
    itemType: PropTypes.oneOf(['project', 'event']),
    itemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    itemName: PropTypes.string
  })
};

/**
 * Shared page skeleton for the public microsites (/share/project/:id, /share/event/:id).
 * Pages supply only their data-specific pieces: hero copy/pills, the description card and copy.
 */
export default function MicrositeShell(props) {
  const {
    pageTitle,
    primaryHref,
    primaryLabel,
    isLoading,
    isError,
    notFoundMessage,
    hasData,
    hero = {},
    description,
    showCtas = true,
    primaryCta = {},
    interestForm = {}
  } = props;

  return (
    <div className="bg-white">
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <RemoveLoadingScreen/>

      <div className="sticky top-0 z-30">
        <TopNavbar primaryHref={primaryHref} primaryLabel={primaryLabel} onSecondaryClick={scrollToForm}/>
      </div>

      {isLoading && (
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader/>
        </div>
      )}

      {isError && (
        <div className="min-h-[60vh] flex items-center justify-center text-cs-gray-500">
          {notFoundMessage}
        </div>
      )}

      {hasData && (
        <>
          <MicrositeHero {...hero}/>

          {/* Cream band — hero cards overlap up into the hero; wraps details + CTAs */}
          <div className="bg-cs-yellow-100">
            <section className={`${containerPx} -mt-[88px] relative z-10 pb-12`}>
              <MicrositeHeroCards/>
            </section>

            <section className={`${containerPx} pb-12`}>
              {description}
            </section>

            {/* Primary CTA — Ready to get involved */}
            {showCtas && <section className={`${containerPx} pb-8`}>
              <CtaGroup type="primary" body={primaryCta.body} primaryHref={primaryCta.href || '/signup'}/>
            </section>}

            {/* Inverted CTA — Interested but not ready; closes the cream band */}
            {showCtas && <section className={`${containerPx} pb-[100px]`}>
              <CtaGroup type="inverted" onButtonClick={scrollToForm}/>
            </section>}
          </div>

          {/* Static marketing sections */}
          <MarketingContent/>

          {/* FAQ, CTA + interest form */}
          <MicrositeFaqSection
            onButtonClick={scrollToForm}
            itemType={interestForm.itemType}
            itemId={interestForm.itemId}
            itemName={interestForm.itemName}
          />

          <MarketingFooter/>
        </>
      )}
    </div>
  );
}
